import { type CreateAirportCategorizationInput } from '../../API';
import { type DbCreated } from '../../Datasource/LocalDB/types';
import { isNone, isSome } from '../../utils';
import { type CompleteAirport } from '../Datasources/Airports';
import { type EventResource } from '../Scheduling/types';
import { findAircraftResource } from '../Scheduling/types';

export type AirportCategorization = {
    id: string;
    name: string;
    icao: string;
    categorizationData: AirportCategorizationData[];
    _version: number | undefined;
};

export type GeneralAirportCategorizationData = {
    // appliesOnlyToTypes: null, // deprecated
    appliesOnlyToAircraftTypeIds: null; // replaces appliesOnlyToTypes
    instrumentApproachTypes: string[];
    nonStandardApproach: boolean;
    nightCapable: boolean;
    specialBriefing: boolean;
    unusualWxOrObstacles: boolean;
    circlingHighMdaOrRoute: boolean;
    texts: CategorizationTexts<GeneralAirportCategorizationData>;
    lastVisitIsTypeSpecific?: boolean;
};

export type AirportCategoryLetter = 'A' | 'B' | 'BC' | 'C' | 'X'; // X is for not approved

export type TypeSpecificAirportCategorizationData = {
    appliesOnlyToAircraftTypeIds: string[]; // replaces appliesOnlyToTypes
    specialEngineOutProcedure: boolean;
    runwayWithPerformanceLimitation: boolean;
    opsPermitted: 'cat' | 'nc' | 'none';
    specialTraining: boolean;
    specialTrainingIsOptional: boolean;
    category: AirportCategoryLetter;
    texts: CategorizationTexts<TypeSpecificAirportCategorizationData>;
    nonstandardRevalidationPeriod?: number;
    certificateRequired: boolean;
    certificateRevalidationPeriod?: number | null; // number - months, null - valid permanently, undefined - same as NonstandardRevalidationPeriod
};

export type CategorizationTexts<T> = {
    [P in keyof T]?: string;
};

export type AirportCategorizationData = GeneralAirportCategorizationData | TypeSpecificAirportCategorizationData;

export const categorizationIsGeneral = (
    categorization: AirportCategorizationData
): categorization is GeneralAirportCategorizationData =>
    categorization.appliesOnlyToAircraftTypeIds === null || (categorization as any).appliesOnlyToTypes === null; // back-compat;

export function categorizationIsTypeSpecific(
    categorization: AirportCategorizationData
): categorization is TypeSpecificAirportCategorizationData {
    return !categorizationIsGeneral(categorization);
}

export const airportCategorizationFromDb = (
    input: CreateAirportCategorizationInput & DbCreated
): AirportCategorization => {
    const categorizationData = JSON.parse(input.categorizationData) as AirportCategorizationData[];
    categorizationData.forEach((d: AirportCategorizationData) => {
        // back-compat
        if (categorizationIsGeneral(d)) {
            if (d.instrumentApproachTypes === undefined) {
                d.instrumentApproachTypes = [];
            }
            if ((d.appliesOnlyToAircraftTypeIds as any) !== null) {
                d.appliesOnlyToAircraftTypeIds = null; // back-compat
            }
        } else {
            if (d.certificateRequired === undefined) {
                d.certificateRequired = false;
            }
            if ((d.appliesOnlyToAircraftTypeIds as any) === undefined) {
                d.appliesOnlyToAircraftTypeIds = []; // back-compatibility
            }
        }

        if (d.texts === undefined) {
            d.texts = {};
        }
    });

    return {
        id: input.id,
        name: input.name,
        icao: input.icao,
        categorizationData,
        _version: input._version,
    };
};

export const airportCategorizationToDb = (input: AirportCategorization): CreateAirportCategorizationInput => ({
    id: input.id,
    name: input.name,
    icao: input.icao,
    categorizationData: JSON.stringify(input.categorizationData),
    _version: input._version,
});

export const getMinimumCategory = (
    general: GeneralAirportCategorizationData,
    specific: TypeSpecificAirportCategorizationData
): TypeSpecificAirportCategorizationData['category'] => {
    if (specific.opsPermitted === 'none') {
        return 'X';
    }
    if (specific.specialTraining) {
        if (specific.specialTrainingIsOptional) {
            return 'BC';
        }

        return 'C';
    }
    if (
        general.nonStandardApproach ||
        general.circlingHighMdaOrRoute ||
        !general.nightCapable ||
        specific.runwayWithPerformanceLimitation ||
        general.specialBriefing ||
        specific.specialEngineOutProcedure ||
        general.instrumentApproachTypes.length === 0 ||
        general.unusualWxOrObstacles
    ) {
        return 'B';
    }

    return 'A';
};

export const generalAirportCategorizationLabels: CategorizationLables<
    Omit<GeneralAirportCategorizationData, 'appliesOnlyToAircraftTypeIds' | 'texts'>
> = {
    instrumentApproachTypes: 'Available Instrument Approach Types',
    nonStandardApproach: 'Non-Standard Approach',
    nightCapable: 'Night Capable',
    specialBriefing: 'Briefing Reqd.',
    unusualWxOrObstacles: 'Unusual WX/Obstacles',
    circlingHighMdaOrRoute: 'High Circling MDA or Prescribed Route',
    lastVisitIsTypeSpecific: 'Last Visit is Type Specific',
};

export const typeSpecificCategorizationLabels: CategorizationLables<
    Omit<TypeSpecificAirportCategorizationData, 'appliesOnlyToAircraftTypeIds' | 'texts'>
> = {
    specialEngineOutProcedure: 'Special Engine Out Procedure',
    runwayWithPerformanceLimitation: 'Runway with Performance Limitation',
    opsPermitted: 'Operations Permitted',
    specialTraining: 'Training Reqd.',
    specialTrainingIsOptional: 'Optional operation w/o training',
    category: 'Category',
    nonstandardRevalidationPeriod: 'Revalidation Period [months]',
    certificateRequired: 'Briefing Certificate Upload Required',
    certificateRevalidationPeriod: 'Certificate Revalidation Period',
};

type CategorizationLables<T> = {
    [P in keyof T]: string;
};

export const categorizationIsBriefable = (categorization: TypeSpecificAirportCategorizationData): boolean =>
    !['A', 'X'].includes(categorization.category);

export function getAirportCategorizations(
    completeAirport: CompleteAirport | undefined
): AirportCategorization[] | undefined {
    return completeAirport?.categorizations.items?.map(airportCategorizationFromDb);
}

export function getTypeSpecificAirportCategorizationData(params: {
    completeAirport: CompleteAirport | undefined;
    aircraftTypeId: string | undefined;
}): TypeSpecificAirportCategorizationData | undefined {
    const { completeAirport, aircraftTypeId } = params;
    if (isNone(aircraftTypeId)) {
        return undefined;
    }

    const categorizations = getAirportCategorizations(completeAirport);

    return categorizations?.reduce<TypeSpecificAirportCategorizationData | undefined>((acc, item) => {
        if (isSome(acc)) {
            return acc;
        }
        const cat = item.categorizationData.find((c) => c.appliesOnlyToAircraftTypeIds?.includes(aircraftTypeId));
        if (isSome(cat) && categorizationIsTypeSpecific(cat)) {
            return cat;
        }

        return undefined;
    }, undefined);
}

export function getAirportCategoryForAircraftTypeId(params: {
    completeAirport: CompleteAirport | undefined;
    aircraftTypeId: string | undefined;
}): AirportCategoryLetter | undefined {
    const { completeAirport, aircraftTypeId } = params;

    return getTypeSpecificAirportCategorizationData({ completeAirport, aircraftTypeId })?.category;
}

export function getBriefingCertificateRequiredForAircraftTypeId(params: {
    completeAirport: CompleteAirport | undefined;
    aircraftTypeId: string | undefined;
}) {
    const { completeAirport, aircraftTypeId } = params;

    return getTypeSpecificAirportCategorizationData({ completeAirport, aircraftTypeId })?.certificateRequired;
}

export function getAirportCategorizationInfo(params: {
    completeAirport: CompleteAirport | undefined;
    reservationResources: EventResource[];
}) {
    const { completeAirport, reservationResources } = params;
    const aircraftTypeId = findAircraftResource(reservationResources)?.resourceTypeId;
    const categoryLetter = getAirportCategoryForAircraftTypeId({ completeAirport, aircraftTypeId });
    const briefingCertificateUploadRequired = getBriefingCertificateRequiredForAircraftTypeId({
        completeAirport,
        aircraftTypeId,
    });

    return { categoryLetter, briefingCertificateUploadRequired };
}
